import { isAusDeal } from "@/lib/disbursementLock";

/**
 * The vendor a disbursement went out, or would go out, on, as named in the copy.
 *
 * `status.rail` is decided by the server (a stored payment id first, else the deal's
 * region); a status without one is GoCardless, which every status read before the
 * field existed was.
 */
export function disbursementRail(status) {
  return status?.rail === "Capay" ? "Capay" : "GoCardless";
}

/**
 * The payment id the deal stores on its payout rail, or undefined.
 */
export function disbursementPaymentId(status) {
  return disbursementRail(status) === "Capay" ? status?.capay_payment_id : status?.gc_payment_id;
}

/**
 * What the operator is told when a disbursement send ends without the deal being funded.
 *
 * `status` is the body of `GET /applications/{id}/disbursement-status`. Three outcomes read
 * differently and only the last may be sent again; the server refuses a second send for the
 * other two, so telling the operator to click Disburse Now would send them into a 409:
 *
 *  - `send_unknown`: the create request may have reached the payout rail (GoCardless, or
 *    Capay for an AUS deal) and its outcome is not known (with or without a payment id);
 *  - `failed` with a payment id: the payment exists at the rail (a Confirmation of Payee
 *    refusal, or a connect error on the verify poll or approve call after the create),
 *    created and not confirmed as sent;
 *  - `failed` with no payment id: nothing was created and a retry is safe.
 *
 * Neither locked outcome points at `recover-disbursement`: it has no screen, so the copy
 * sends the operator to an engineer, who records the payment if the money went out. An
 * Admin can release the lock from the disbursement screen if it did not (a payment that
 * exists must first be cancelled or failed in GoCardless, or cancelled, failed or rejected in
 * Capay).
 *
 * `application` is the deal being sent. An AUS deal whose outcome is unknown and records no
 * Capay payment id is checked in Capay by an engineer: the release refuses it, so its copy
 * does not offer an Admin release. A Capay payment id comes from the status
 * (`capay_payment_id`). The status does not carry the Capay payment's own status, so the
 * copy cannot tell a returned payment from a cancelled one: it names the release for
 * the cancelled, failed and rejected cases and says a returned payment stays with an
 * engineer (the Slack notice, which knows the status, names the engineer outright).
 */
export function disbursementFailureMessage(status, application) {
  const error = status?.gc_send_error;
  const paymentId = status?.gc_payment_id;
  const capayPaymentId = status?.capay_payment_id;
  const rail = disbursementRail(status);
  if (paymentId && capayPaymentId) {
    return (
      `The disbursement was not completed: ${error || "unknown error"}. ` +
      `GoCardless payment ${paymentId} and Capay payment ${capayPaymentId} both exist. ` +
      "Do NOT send again. The deal is locked against a second send; contact an engineer."
    );
  }
  if (status?.gc_send_status === "send_unknown") {
    if (capayPaymentId) {
      return (
        `The disbursement outcome is unknown: ${error || "no error returned"}. ` +
        `Do NOT send again. Check Capay for payment ${capayPaymentId} and contact an ` +
        "engineer to record it if it went out; an Admin can release the lock from this " +
        "screen once it is cancelled, failed or rejected in Capay. A returned payment stays " +
        "with an engineer."
      );
    }
    if (isAusDeal(application) || rail === "Capay") {
      return (
        `The disbursement outcome is unknown: ${error || "no error returned"}. ` +
        "Do NOT send again. Check Capay and contact an engineer: they record it if it went " +
        "out, or release the lock if it did not."
      );
    }
    return (
      `The disbursement outcome is unknown: ${error || "no error returned"}. ` +
      `Do NOT send again. Check ${rail}${paymentId ? ` for payment ${paymentId}` : ""} ` +
      "and contact an engineer to record it if it went out; an Admin can release the lock from " +
      "this screen if it did not."
    );
  }
  if (capayPaymentId) {
    return (
      `The disbursement was not completed: ${error || "unknown error"}. ` +
      `Capay payment ${capayPaymentId} EXISTS (created, not confirmed as sent). ` +
      "Check it in Capay and do NOT send again. The deal is locked against a second send. " +
      "Contact an engineer to record it if it went out; an Admin can release the lock from " +
      "this screen once it is cancelled, failed or rejected in Capay. A returned payment stays " +
      "with an engineer."
    );
  }
  if (paymentId) {
    return (
      `The disbursement was not completed: ${error || "unknown error"}. ` +
      `GoCardless payment ${paymentId} EXISTS (created, not confirmed as sent). ` +
      "Check it in GoCardless and do NOT send again. The deal is locked against a second send. " +
      "Contact an engineer to record it if it went out; an Admin can release the lock from " +
      "this screen once it is cancelled or failed in GoCardless."
    );
  }
  return (
    `${rail} rejected the disbursement: ${error || "unknown error"}. ` +
    `No payment was created. Fix the issue in ${rail} and click Disburse Now again.`
  );
}
